import { ref, watch, computed, unref, onBeforeUnmount, readonly, onUnmounted } from 'vue';
import { ROLE_PERMISSIONS, mergePermissions, PERMISSION_KEYS } from '@donoor/types/team';

// src/useSearch.ts
function useSearch(items, matchFn, options = {}) {
  const { debounceMs = 0, initialQuery = "" } = options;
  const searchQuery = ref(initialQuery);
  const debouncedQuery = ref(initialQuery);
  let timer = null;
  watch(searchQuery, (val) => {
    if (debounceMs <= 0) {
      debouncedQuery.value = val;
      return;
    }
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      debouncedQuery.value = val;
    }, debounceMs);
  });
  const filteredItems = computed(() => {
    const list = unref(items) || [];
    const q = debouncedQuery.value.trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) => matchFn(item, q));
  });
  const isFiltering = computed(() => debouncedQuery.value.trim().length > 0);
  const reset = () => {
    searchQuery.value = "";
    debouncedQuery.value = "";
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return {
    searchQuery,
    debouncedQuery,
    filteredItems,
    reset,
    isFiltering
  };
}
function useDebounce(source, delayMs = 300) {
  const debounced = ref(source.value);
  let timer = null;
  watch(source, (val) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      debounced.value = val;
    }, delayMs);
  });
  return debounced;
}
function useDebouncedFn(fn, delayMs = 300) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delayMs);
  };
}
var lockCount = 0;
var savedScrollY = 0;
var savedBodyStyle = null;
function applyLock() {
  if (typeof document === "undefined") return;
  if (lockCount === 0) {
    savedScrollY = window.scrollY;
    savedBodyStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width
    };
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.width = "100%";
  }
  lockCount += 1;
}
function releaseLock() {
  if (typeof document === "undefined") return;
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0 && savedBodyStyle) {
    document.body.style.overflow = savedBodyStyle.overflow;
    document.body.style.position = savedBodyStyle.position;
    document.body.style.top = savedBodyStyle.top;
    document.body.style.width = savedBodyStyle.width;
    window.scrollTo({ top: savedScrollY, behavior: "instant" });
    savedBodyStyle = null;
  }
}
function useScrollLock(isLocked) {
  if (isLocked) {
    let appliedByThis = false;
    watch(
      isLocked,
      (val) => {
        if (val && !appliedByThis) {
          applyLock();
          appliedByThis = true;
        } else if (!val && appliedByThis) {
          releaseLock();
          appliedByThis = false;
        }
      },
      { immediate: true }
    );
    onBeforeUnmount(() => {
      if (appliedByThis) {
        releaseLock();
        appliedByThis = false;
      }
    });
  }
  return {
    lock: applyLock,
    unlock: releaseLock,
    isLocked: () => lockCount > 0
  };
}
function useWizard(totalSteps, options = {}) {
  if (totalSteps < 1) {
    throw new Error("useWizard: totalSteps must be >= 1");
  }
  const initialStep = options.initialStep ?? 1;
  if (initialStep < 1 || initialStep > totalSteps) {
    throw new Error(
      `useWizard: initialStep ${initialStep} out of range [1, ${totalSteps}]`
    );
  }
  const currentStep = ref(initialStep);
  const transitionDir = ref("forward");
  const progressPercent = computed(
    () => Math.round(currentStep.value / totalSteps * 100)
  );
  const isFirstStep = computed(() => currentStep.value === 1);
  const isLastStep = computed(() => currentStep.value === totalSteps);
  const canGoBack = computed(() => currentStep.value > 1);
  const canGoNext = computed(() => currentStep.value < totalSteps);
  async function next() {
    if (!canGoNext.value) return;
    const from = currentStep.value;
    const to = from + 1;
    if (options.beforeNext) {
      const allowed = await options.beforeNext(from, to);
      if (allowed === false) return;
    }
    transitionDir.value = "forward";
    currentStep.value = to;
    options.onStepChange?.(to, "forward");
  }
  async function back() {
    if (!canGoBack.value) return;
    const from = currentStep.value;
    const to = from - 1;
    if (options.beforeBack) {
      const allowed = await options.beforeBack(from, to);
      if (allowed === false) return;
    }
    transitionDir.value = "backward";
    currentStep.value = to;
    options.onStepChange?.(to, "backward");
  }
  function goTo(step) {
    if (step < 1 || step > totalSteps) return;
    if (step === currentStep.value) return;
    transitionDir.value = step > currentStep.value ? "forward" : "backward";
    currentStep.value = step;
    options.onStepChange?.(step, transitionDir.value);
  }
  function reset() {
    currentStep.value = initialStep;
    transitionDir.value = "forward";
  }
  return {
    currentStep: readonly(currentStep),
    totalSteps,
    transitionDir: readonly(transitionDir),
    progressPercent,
    isFirstStep,
    isLastStep,
    canGoBack,
    canGoNext,
    next,
    back,
    goTo,
    reset
  };
}
function useSlugAvailability(slug, checker, options = {}) {
  const minLength = options.minLength ?? 2;
  const debounceMs = options.debounceMs ?? 400;
  const errorFallback = options.errorFallback ?? null;
  const isChecking = ref(false);
  const isAvailable = ref(null);
  let timer = null;
  async function runCheck(value) {
    if (!value || value.length < minLength) {
      isChecking.value = false;
      isAvailable.value = null;
      return;
    }
    try {
      const available = await checker(value);
      isAvailable.value = available;
    } catch {
      isAvailable.value = errorFallback;
    } finally {
      isChecking.value = false;
    }
  }
  function scheduleCheck(value) {
    if (timer) clearTimeout(timer);
    if (!value || value.length < minLength) {
      isChecking.value = false;
      isAvailable.value = null;
      return;
    }
    isChecking.value = true;
    isAvailable.value = null;
    timer = setTimeout(() => {
      void runCheck(value);
    }, debounceMs);
  }
  watch(slug, (val) => {
    scheduleCheck(val);
  });
  function recheck() {
    scheduleCheck(slug.value);
  }
  return {
    isChecking,
    isAvailable,
    recheck
  };
}
function useAnimatedNumber(initialValue = 0, duration = 800) {
  const displayValue = ref(initialValue);
  const targetValue = ref(initialValue);
  let animationFrame = null;
  function animateTo(newValue) {
    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
    }
    const startValue = displayValue.value;
    const diff = newValue - startValue;
    if (diff === 0) return;
    const startTime = performance.now();
    targetValue.value = newValue;
    function step(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      displayValue.value = Math.round(startValue + diff * easeOut);
      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
      } else {
        displayValue.value = newValue;
        animationFrame = null;
      }
    }
    animationFrame = requestAnimationFrame(step);
  }
  function setValue(newValue, animate = true) {
    if (animate) {
      animateTo(newValue);
    } else {
      displayValue.value = newValue;
      targetValue.value = newValue;
    }
  }
  onUnmounted(() => {
    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
    }
  });
  return {
    displayValue,
    targetValue,
    setValue,
    animateTo
  };
}
function usePermissions(role, opts = {}) {
  const isAdmin = computed(() => Boolean(unref(opts.isAdminSession)));
  const effectiveRole = computed(
    () => isAdmin.value ? "owner" : unref(role) ?? null
  );
  const permissions = computed(() => {
    if (isAdmin.value) {
      return { ...ROLE_PERMISSIONS.owner };
    }
    const provided = unref(opts.permissions);
    if (provided) {
      return provided;
    }
    const r = unref(role) ?? null;
    if (r) {
      return mergePermissions(
        ROLE_PERMISSIONS[r],
        unref(opts.customPermissions) ?? null
      );
    }
    return null;
  });
  const can = (permission) => computed(() => permissions.value?.[permission] ?? false);
  const canFlags = {};
  for (const key of PERMISSION_KEYS) {
    canFlags[key] = can(key);
  }
  const isOwner = computed(() => effectiveRole.value === "owner");
  const isAdminOrOwner = computed(
    () => effectiveRole.value === "owner" || effectiveRole.value === "admin"
  );
  return {
    ...canFlags,
    role: effectiveRole,
    isAdmin,
    isOwner,
    isAdminOrOwner,
    permissions,
    can
  };
}

export { useAnimatedNumber, useDebounce, useDebouncedFn, usePermissions, useScrollLock, useSearch, useSlugAvailability, useWizard };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map